import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const PLAN_LIMITS: Record<string, number> = {
  free: 10,
  pro: 200,
  agency: 500,
};

// Admin allowlist — these emails are treated as Pro for demo purposes
const ADMIN_EMAILS = ["dawnrebecca@live.co.uk"];

export function useUserPlan() {
  const { user } = useAuth();
  const [plan, setPlan] = useState<string>("free");
  const [reportCount, setReportCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const isAdmin = !!user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      const [planRes, countRes, subRes] = await Promise.all([
        supabase.from("user_plans").select("plan").eq("user_id", user.id).maybeSingle(),
        supabase.from("reports").select("id", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("subscriptions").select("status, current_period_end").eq("user_id", user.id).maybeSingle(),
      ]);

      // Determine plan from subscriptions table first, fall back to user_plans
      const sub = subRes.data;
      const hasActiveSub =
        sub?.status === "active" &&
        (!sub?.current_period_end || new Date(sub.current_period_end) > new Date());

      setPlan(hasActiveSub ? "pro" : planRes.data?.plan || "free");
      setReportCount(countRes.count || 0);
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const effectivePlan = isAdmin ? "pro" : plan;
  const limit = PLAN_LIMITS[effectivePlan] || 10;
  const canGenerate = reportCount < limit;

  return { plan: effectivePlan, reportCount, limit, canGenerate, loading, isAdmin };
}
